import { createContext, useContext, useEffect, useState } from "react";
import { Items } from "@definitions/items";
import { useAuth } from "@hooks/AuthProvider";
import { isSignedIn } from "@utils/auth";
import { synchronise, poll as pollAPI } from "@api/items";
import { throttle } from "lodash";
import { mergeItems } from "@utils/state";

type ItemContextType = {
  items: Items,
  setItems: (items:Items) => void,
}

const ItemContext = createContext({
  items: {},
  setItems: (items:Items) => {}
});

export const useItems = ():ItemContextType => {
  const { items, setItems } = useContext(ItemContext);

  return { items, setItems };
}

const ItemProvider = ({ children }: any) => {
  const [ items, setItems ] = useState<Items>({});
  const [ mounting, setMounting ] = useState(true);
  const { auth } = useAuth();
  const [ interval, setIntervalRef ] = useState<NodeJS.Timeout | null>(null)

  useEffect(() => {
    try {
      const storedItems = localStorage.getItem('items');

      if (storedItems && !isSignedIn(auth)) {
        setItems(JSON.parse(storedItems));
      }
      poll()

      setMounting(false);
    } catch {
      console.error("Bad state. Clearing localStorage. Previous data: ", localStorage.getItem("items"));
      localStorage.setItem("items", "{}");
    }
  }, [])

  useEffect(() => {
    setIntervalRef(setInterval(() => {
      poll()
    }, 60000))

    return stopInterval
  }, [])

  const stopInterval = () => {
    if(!interval) { return }
    clearInterval(interval)
  }

  useEffect(() => { poll() }, [auth.token])

  const poll = async () => {
    if(!isSignedIn(auth)) { return }

    const { success, payload } = await pollAPI(auth);
    if(!success) { return }

    const mergedItems = mergeItems(items, (payload.data as { items:Items }).items)
    setItems(mergedItems)
  }

  useEffect(() => {
    // Don't let the initial mount trigger a save as it clobbers saved data.
    if (mounting) { return; }
    localStorage.setItem('items', JSON.stringify(items));
    if(isSignedIn(auth)) {
      const sync = () => synchronise(items, auth);
      throttle(sync, 1000)()
    }
  }, [items])

  return (
    <ItemContext.Provider value={{ items, setItems }}>
      {children}
    </ItemContext.Provider>
  );
}

export default ItemProvider;